import { CouponType } from "../NormasTeachingCoupon/CouponType.ts";
import type { NormasTeachingCouponDTO } from "../NormasTeachingCoupon/useNormasTeachingCouponById.ts";

export function allocateProportionally(
  totalInCents: number,
  weights: number[],
): number[] {
  if (weights.length === 0) return [];
  const weightsSum = weights.reduce((sum, w) => sum + w, 0);
  const effectiveWeights = weightsSum > 0 ? weights : weights.map(() => 1);
  const effectiveSum = weightsSum > 0 ? weightsSum : weights.length;
  const exactShares = effectiveWeights.map(
    (w) => (totalInCents * w) / effectiveSum,
  );
  const shares = exactShares.map((s) => Math.floor(s));
  let remainder = totalInCents - shares.reduce((sum, s) => sum + s, 0);
  const byLargestFraction = exactShares
    .map((s, index) => ({ index, fraction: s - Math.floor(s) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const { index } of byLargestFraction) {
    if (remainder <= 0) break;
    shares[index] += 1;
    remainder -= 1;
  }
  return shares;
}

export type BonificoPricingPreview = {
  listPriceInCents: number;
  couponDiscountInCents: number;
  amountPaidInCents: number;
  bundleProductAmountsInCents: number[];
};

export function calculateBonificoPricingPreview({
  listPriceInCents,
  coupon,
  amountOverrideInCents,
  bundleProductListPricesInCents = [],
}: {
  listPriceInCents: number;
  coupon?: NormasTeachingCouponDTO | null;
  amountOverrideInCents?: number;
  bundleProductListPricesInCents?: number[];
}): BonificoPricingPreview {
  const couponDiscountInCents = coupon
    ? Math.min(
        coupon.value.couponType === CouponType.Fixed
          ? coupon.value.amount
          : Math.round((listPriceInCents * coupon.value.amount) / 100),
        listPriceInCents,
      )
    : 0;
  const amountPaidInCents =
    amountOverrideInCents ?? listPriceInCents - couponDiscountInCents;
  return {
    listPriceInCents,
    couponDiscountInCents,
    amountPaidInCents,
    bundleProductAmountsInCents: allocateProportionally(
      amountPaidInCents,
      bundleProductListPricesInCents,
    ),
  };
}
